/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Student assignment sections styles
 *
 * @file studentAssignmentSectionsStyles.js
 * @author Gladson Samuel S
 * @since 3.1.0
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const resetButton = {
  appearance: 'none',
  background: 'none',
  border: 'none',
  margin: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  cursor: 'pointer'
};

const focusRing = {
  '&:focus-visible': {
    outline: `2px solid ${mixins.navyColor}`,
    outlineOffset: 2
  }
};

const studentAssignmentSectionsStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 16,
    borderRadius: 24,
    backgroundColor: mixins.lightThemeBgColor2,
    boxSizing: 'border-box',
    width: '100%'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  sectionHeading: {
    margin: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit'
  },
  sectionToggle: {
    ...resetButton,
    ...focusRing,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
    width: '100%',
    minHeight: 40,
    borderRadius: 12,
    boxSizing: 'border-box'
  },
  sectionTitle: {
    padding: '0 8px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary
  },
  sectionChevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    marginRight: 8,
    borderRadius: 1000,
    color: mixins.navyColor,
    '& svg': {
      width: 24,
      height: 24,
      transition: 'transform 0.2s ease'
    }
  },
  sectionChevronCollapsed: {
    '& svg': {
      transform: 'rotate(180deg)'
    }
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  }
};

export const classes = createScopedClasses('aet-student-assignment-sections', studentAssignmentSectionsStyles);

export const scopedStyles = createScopedStyles(studentAssignmentSectionsStyles, classes);

export const StudentAssignmentSectionsStylesRoot = styled('div')(scopedStyles);

export default studentAssignmentSectionsStyles;
